import { differenceInCalendarDays } from 'date-fns';

// Coverage/plan end dates are always authored at UTC midnight — either straight from the
// middleware feed or via the client-side `add(...).toISOString()` fallback in plan.service.ts —
// so they encode a calendar DATE, not a specific moment. Comparing raw timestamps (or even UTC
// calendar days — tried first, and still wrong) breaks down in negative-UTC-offset timezones
// (all of North America): by evening local time, "today" is already tomorrow in UTC, so a
// renewal date that's genuinely one calendar day away collapses to "today" (or earlier). The
// fix: read the target's intended calendar date straight off its UTC Y/M/D (that's what was
// encoded), then compare it against the CALLER's own local calendar date — never convert either
// side through a shared timezone, since there isn't one that's correct for both.
function targetCalendarDateFromUtcFields(date: Date): Date {
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function localCalendarDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * The calendar date encoded in `value`, as a local-midnight Date. Reads the `yyyy-MM-dd` part of an
 * ISO string verbatim (ignoring any time/offset) so the result is the same in every timezone;
 * falls back to the UTC Y/M/D fields for Date inputs or non-ISO strings.
 */
export function calendarDateOf(value: string | Date): Date {
  if (typeof value === 'string') {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) {
      return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
  }
  return targetCalendarDateFromUtcFields(typeof value === 'string' ? new Date(value) : value);
}

/**
 * Number of calendar days between `now` (the caller's local "today") and `targetDate` (a
 * UTC-midnight-encoded calendar date). Positive = targetDate is in the future; 0 = targetDate is
 * today; negative = targetDate has passed.
 */
export function daysUntil(targetDate: string | Date, now: Date = new Date()): number {
  return differenceInCalendarDays(calendarDateOf(targetDate), localCalendarDate(now));
}
